// ─── FORM D — private-market intelligence ─────────────────────────────────────
// Reads the SEC EDGAR Form D endpoints (cache-first backend, 4h):
//   GET /api/formD/recent  — normalized recent filings
//   GET /api/formD/trends  — 30/60/90-day volume + industry breakdown
// Deep Obsidian / Quantum Emerald, JetBrains Mono — matches the terminal panels.

import { useState, useEffect, useMemo } from "react";
import { C, s, font, backendFetch, AnalystButton } from "./core";
import { SearchBox } from "./components/SearchBox";

// Offering amount → $XM / $XB. "Indefinite" (pooled funds) passes through as-is.
function fmtAmount(a) {
  if (a == null) return "—";
  if (typeof a === "string") return a; // "Indefinite"
  if (a >= 1e9) return `$${(a / 1e9).toFixed(a >= 1e10 ? 0 : 1)}B`;
  if (a >= 1e6) return `$${(a / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M`;
  if (a >= 1e3) return `$${Math.round(a / 1e3)}K`;
  return `$${a}`;
}

const fmtCount = (n) => (n == null ? "—" : n >= 10000 ? "10,000+" : n.toLocaleString());

const EXEMPTION_COLOR = { "506(c)": C.amber, "506(b)": C.teal };
const exColor = (e) => EXEMPTION_COLOR[e] || C.textDim;

// Search text: lower-case, whitespace collapsed. Plain substring match — the
// query is never a regex, so "(", "*" or "." are just characters.
const norm = (v) => String(v ?? "").toLowerCase().replace(/\s+/g, " ").trim();

const MIN_OPTIONS = [
  { label: "ALL", v: 0 },
  { label: "$1M+", v: 1e6 },
  { label: "$5M+", v: 5e6 },
  { label: "$10M+", v: 10e6 },
  { label: "$50M+", v: 50e6 },
];

export function FormDPanel({ keys = {} }) {
  const [recent,  setRecent]  = useState([]);
  const [trends,  setTrends]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(false);

  const [sort,   setSort]   = useState({ key: "filed_date", dir: "desc" });
  const [fIndustry, setFIndustry] = useState("");
  const [fState,    setFState]    = useState("");
  const [fMin,      setFMin]      = useState(0);
  const [win,       setWin]       = useState("30");
  const [query,     setQuery]     = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(false);
      const [r, t] = await Promise.allSettled([
        backendFetch("/api/formD/recent?limit=200", "/api/formD/recent?limit=200"),
        backendFetch("/api/formD/trends", "/api/formD/trends"),
      ]);
      if (cancelled) return;
      try {
        if (r.status === "fulfilled" && r.value.ok) {
          const data = await r.value.json();
          setRecent(Array.isArray(data) ? data : (data.data || []));
        } else setError(true);
      } catch { setError(true); }
      try {
        if (t.status === "fulfilled" && t.value.ok) setTrends(await t.value.json());
      } catch { /* trends optional */ }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Filter option lists derived from the data.
  const industries = useMemo(
    () => [...new Set(recent.map((f) => f?.industry).filter(Boolean))].sort(),
    [recent]
  );
  const states = useMemo(
    () => [...new Set(recent.map((f) => f?.state).filter(Boolean))].sort(),
    [recent]
  );

  // ⚠ CLIENT-SIDE, AND LABELLED AS SUCH. The backend holds only the most recent
  // filings it fetched (at most 120 of the thousands in its 45-day window), so the
  // search covers what is loaded and the label says exactly how many that is.
  const q = norm(query);
  const loadedCount = recent.filter(Boolean).length;

  // Apply search + filters + sort.
  const rows = useMemo(() => {
    let out = recent.filter(Boolean);
    if (q) out = out.filter((f) => [f.issuer_name, f.industry, f.state].some((v) => norm(v).includes(q)));
    if (fIndustry) out = out.filter((f) => f.industry === fIndustry);
    if (fState)    out = out.filter((f) => f.state === fState);
    if (fMin)      out = out.filter((f) => typeof f.offering_amount === "number" && f.offering_amount >= fMin);
    const dir = sort.dir === "asc" ? 1 : -1;
    const val = (f) => {
      if (sort.key === "offering_amount") {
        const a = f.offering_amount;
        return typeof a === "number" ? a : -1; // Indefinite/None sort low
      }
      return f.filed_date || "";
    };
    return [...out].sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0) * dir);
  }, [recent, q, fIndustry, fState, fMin, sort]);

  const toggleSort = (key) =>
    setSort((p) => (p.key === key ? { key, dir: p.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));
  const sortArrow = (key) => (sort.key === key ? (sort.dir === "desc" ? " ▾" : " ▴") : "");

  // Industry heat (from trends.by_industry).
  const heat = useMemo(() => {
    const bi = trends?.by_industry;
    if (!bi || typeof bi !== "object") return [];
    const entries = Object.entries(bi).filter(([, n]) => n > 0);
    const max = entries.reduce((m, [, n]) => Math.max(m, n), 0) || 1;
    return entries.map(([label, n]) => ({ label, n, pct: n / max })).slice(0, 12);
  }, [trends]);

  // AI context — top 5 industries + largest raises in the current view.
  const analystContext = useMemo(() => {
    const topInd = heat.slice(0, 5).map((h) => `${h.label} (${h.n})`).join(", ") || "n/a";
    const big = [...recent]
      .filter((f) => typeof f?.offering_amount === "number")
      .sort((a, b) => b.offering_amount - a.offering_amount)
      .slice(0, 5)
      .map((f) => `${f.issuer_name} — ${fmtAmount(f.offering_amount)} (${f.industry}, ${f.exemption_type})`)
      .join("; ") || "n/a";
    const c506 = recent.filter((f) => f?.exemption_type === "506(c)").length;
    const b506 = recent.filter((f) => f?.exemption_type === "506(b)").length;
    return (
      `SEC Form D private-market filings snapshot (${recent.length} recent filings ≥ $500K):\n\n` +
      `Top industries by volume: ${topInd}\n` +
      `Largest raises: ${big}\n` +
      `Exemption mix: ${c506} × 506(c) (publicly solicited) / ${b506} × 506(b) (accredited-only)\n` +
      `Filing volume — 30d: ${fmtCount(trends?.counts?.["30"])}, 60d: ${fmtCount(trends?.counts?.["60"])}, 90d: ${fmtCount(trends?.counts?.["90"])}`
    );
  }, [recent, heat, trends]);

  const selStyle = {
    ...s.inp, width: "auto", padding: "4px 8px", fontSize: 11, cursor: "pointer",
  };
  const th = (label, key) => (
    <th
      onClick={key ? () => toggleSort(key) : undefined}
      style={{
        textAlign: key === "offering_amount" || key === "amount_sold" ? "right" : "left",
        padding: "6px 8px", fontSize: 9, letterSpacing: "0.08em", color: C.textDim,
        borderBottom: `1px solid ${C.border}`, cursor: key ? "pointer" : "default",
        whiteSpace: "nowrap", userSelect: "none",
      }}
    >
      {label}{key ? sortArrow(key) : ""}
    </th>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "0.12em", color: C.text }}>
          PRIVATE MARKETS
        </div>
        <div style={{ fontSize: 11, color: C.textDim, lineHeight: 1.6, marginTop: 3 }}>
          Reg D exempt offerings ≥ $500K · issuer, size, exemption, industry ·{" "}
          <span style={{ color: C.textDim }}>updates every 4h</span>
        </div>
      </div>

      <SearchBox value={query} onChange={setQuery} placeholder="Search by issuer, industry or state..." />
      {q && (
        <div style={{ fontSize: 10, color: C.textDim, marginBottom: 8 }}>
          Searching the {loadedCount} most recent filings ·{" "}
          <span style={{ color: C.text }}>{rows.length}</span> {rows.length === 1 ? "match" : "matches"}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <select style={selStyle} value={fIndustry} onChange={(e) => setFIndustry(e.target.value)}>
          <option value="">All industries</option>
          {industries.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <select style={selStyle} value={fState} onChange={(e) => setFState(e.target.value)}>
          <option value="">All states</option>
          {states.map((st) => <option key={st} value={st}>{st}</option>)}
        </select>
        <div style={{ display: "flex", gap: 4 }}>
          {MIN_OPTIONS.map((o) => (
            <button
              key={o.label}
              onClick={() => setFMin(o.v)}
              style={{
                ...s.btn(fMin === o.v ? "amber" : "default"),
                padding: "4px 9px", fontSize: 10,
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ ...s.panel, padding: 20, color: C.textDim, fontSize: 12 }}>Loading Form D filings…</div>
      ) : error && recent.length === 0 ? (
        <div style={{ ...s.panel, padding: 20, color: C.textDim, fontSize: 12 }}>
          Form D data is unavailable right now. It refreshes on the next request.
        </div>
      ) : (
        <>
          {/* ── SECTION 1 — recent filings table ── */}
          <div style={s.panel}>
            <div style={s.panelH}>
              <span style={s.panelT}>RECENT FILINGS</span>
              <span style={{ fontSize: 9, color: C.textDim }}>{rows.length} shown</span>
            </div>
            <div style={{ ...s.panelB, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: font }}>
                <thead>
                  <tr>
                    {th("ISSUER")}
                    {th("INDUSTRY")}
                    {th("OFFERING", "offering_amount")}
                    {th("SOLD")}
                    {th("FILED", "filed_date")}
                    {th("EXEMPTION")}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={6} style={{ padding: 14, fontSize: 11, color: C.textDim }}>
                      {q
                        ? `No filings match “${query.trim()}” in the ${loadedCount} most recent filings${fIndustry || fState || fMin ? " with the current filters" : ""}.`
                        : "No filings match the current filters."}
                    </td></tr>
                  ) : rows.map((f, i) => (
                    <tr key={`${f.cik}-${i}`} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: "6px 8px", fontSize: 11, color: C.text, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {f.issuer_name || "—"}
                      </td>
                      <td style={{ padding: "6px 8px", fontSize: 10, color: C.textMid, whiteSpace: "nowrap" }}>{f.industry || "—"}</td>
                      <td style={{ padding: "6px 8px", fontSize: 11, color: C.amber, fontWeight: 700, textAlign: "right", whiteSpace: "nowrap" }}>
                        {fmtAmount(f.offering_amount)}
                      </td>
                      <td style={{ padding: "6px 8px", fontSize: 10, color: C.textMid, textAlign: "right", whiteSpace: "nowrap" }}>
                        {typeof f.amount_sold === "number" ? fmtAmount(f.amount_sold) : (f.amount_sold || "—")}
                      </td>
                      <td style={{ padding: "6px 8px", fontSize: 10, color: C.textDim, whiteSpace: "nowrap" }}>{f.filed_date || "—"}</td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        <span style={{
                          fontSize: 9, fontWeight: 700, color: exColor(f.exemption_type),
                          padding: "1px 6px", background: exColor(f.exemption_type) + "18",
                          border: `1px solid ${exColor(f.exemption_type)}33`, borderRadius: 2,
                        }}>
                          {f.exemption_type || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── SECTION 2 — industry heat ── */}
          <div style={s.panel}>
            <div style={s.panelH}>
              <span style={s.panelT}>INDUSTRY HEAT</span>
              <span style={{ display: "flex", gap: 4 }}>
                {["30", "60", "90"].map((w) => (
                  <button key={w} onClick={() => setWin(w)}
                    style={{ ...s.btn(win === w ? "amber" : "default"), padding: "3px 8px", fontSize: 10 }}>
                    {w}D
                  </button>
                ))}
              </span>
            </div>
            <div style={s.panelB}>
              <div style={{ fontSize: 11, color: C.textDim, marginBottom: 10 }}>
                <span style={{ color: C.amber, fontWeight: 700, fontSize: 15 }}>{fmtCount(trends?.counts?.[win])}</span>
                {" "}filings in the last {win} days · bars show current activity by industry
              </div>
              {heat.length === 0 ? (
                <div style={{ fontSize: 11, color: C.textDim }}>Industry breakdown unavailable.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {heat.map((h) => (
                    <div key={h.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 10, color: C.textMid, width: 150, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {h.label}
                      </span>
                      <div style={{ flex: 1, height: 12, background: C.border, borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ width: `${Math.max(4, h.pct * 100)}%`, height: "100%", background: C.amber, opacity: 0.85 }} />
                      </div>
                      <span style={{ fontSize: 10, color: C.textDim, width: 36, textAlign: "right", flexShrink: 0 }}>{h.n}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── SECTION 3 — AI analyst ── */}
          {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && (
            <AnalystButton panel="formD" label="SYNTHESIZE" context={analystContext} />
          )}

          <div style={{ fontSize: 10, color: C.textDim, marginTop: 8, lineHeight: 1.6 }}>
            Form D full-text search + filing XML · 506(c) = publicly solicited, 506(b) = accredited-only ·
            micro-raises below $500K excluded ·{" "}
            <span style={{ color: "#FFD700", fontWeight: 700 }}>Not a recommendation.</span>
          </div>
        </>
      )}
    </div>
  );
}
